import type { FC } from "react"
import { memo } from "react"

import { AnimatePresence, motion } from "framer-motion"
import { useLocation } from "react-router-dom"
import { useRecoilValue, useSetRecoilState } from "recoil"
import styled, { css } from "styled-components"

import { ModuleSelectionDropdownForDesigner_OS } from "~/containers/Proposal/Setup/Form/Steps/ModuleSelection_new"
import type { UseDesign } from "~/containers/Proposal/state/designs/use-design"
import { NotificationBarContainer } from "~/containers/Proposal/state/notifications/NotificationBar"
import { NotificationIconForDesigner } from "~/containers/Proposal/state/notifications/NotificationIcon"
import { proposalRemoteState } from "~/containers/Proposal/state/proposal/proposal-state"
import { currentUserIsDesignRequestUserState } from "~/containers/Proposal/state/user/user-state"
import { useTriggerPreventionToast } from "~/hooks/useTriggerPreventionToast"
import { fontWeight } from "~/utils/styles"

import { ReactComponent as NotePad } from "@svg/note-pad.svg"
import { ReactComponent as RedoArrow } from "@svg/RedoArrow.svg"
import { ReactComponent as UndoArrow } from "@svg/UndoArrow.svg"

import { BackLink } from "./BackLink"
import { MountTypeDropdown } from "./MountTypeDropdown"
import RotatePanelsButton from "./RotatePanelsButton"
import { Settings } from "./Settings"
import { siteRemoteState } from "../../Proposal/state/site/site-state"
import { Gap } from "../../proposal-styles"
import { useDesignerCapabilities } from "../designer-capabilities"
import { showDesignSwitcherState } from "../DesignSwitcher"
import { VersionIcon } from "../DesignSwitcher/VersionIcon"
import { useUndoRedo } from "../state/elements/timeline/transactions"
import { notesDrawerIsOpenState } from "../state/view"

export const StyledTopBar = styled.section`
  display: flex;
  width: 100%;
  flex-direction: row;
  height: 50px;
  gap: 20px;
  align-items: center;
  z-index: calc(var(--z-toolbar) + 1);
  position: relative;
  background: white;
  padding: 15px 20px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
`
const Address = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  * {
    font-size: 14px;
    line-height: 15px;
  }
  label {
    color: var(--grey-dark);
    ${fontWeight(500)}
  }
  span {
    white-space: nowrap;
    color: var(--blue-dark-font);
    ${fontWeight(600)}
  }
`
const Left = styled.div`
  display: flex;
  position: relative;
  width: 240px;
`

const RedoUndoContainer = styled.div`
  display: flex;
  gap: 11px;
`
export const IconButton = styled.button<{ blocked?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--grey-border);
  border-radius: 5px;
  height: 30px;
  width: 30px;
  cursor: pointer;
  svg {
    height: 18px;
    width: 18px;
    path {
      fill: var(--grey-dark);
      transition: fill 0.3s;
    }
  }
  transition: transform 0.1s ease-in-out;
  &:hover {
    transform: scale(1.05);
    path {
      fill: var(--blue-dark-font);
    }
  }

  ${({ blocked }) =>
    blocked &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
      &:hover {
        transform: scale(1);
        path {
          fill: var(--grey-dark);
        }
      }
    `}
`
const Overlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--blue-dark);
  z-index: 99;
`

const TopBar_Internal: FC = () => {
  const { homeowner } = useRecoilValue(proposalRemoteState)
  const { address } = useRecoilValue(siteRemoteState)
  const [undo, redo] = useUndoRedo()
  const { pathname } = useLocation()
  const isServiceBureauRoute = pathname.includes(`service-queue`)
  const setNotesDrawerIsOpen = useSetRecoilState(notesDrawerIsOpenState)
  const currentUserIsDesignUser = useRecoilValue(
    currentUserIsDesignRequestUserState,
  )
  const triggerPreventionToast = useTriggerPreventionToast()

  const {
    designRequest,
    designSwitcher,
    moduleDropdown,
    mountType,
    notifications,
    topBarAccessory,
  } = useDesignerCapabilities()
  const showDesignSwitcher =
    useRecoilValue(showDesignSwitcherState) && designSwitcher

  return (
    <StyledTopBar>
      <AnimatePresence>
        {showDesignSwitcher && (
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.25 }}
            exit={{ opacity: 0 }}
          />
        )}
      </AnimatePresence>
      <Left>
        <BackLink />
      </Left>
      {designSwitcher && !isServiceBureauRoute && currentUserIsDesignUser && (
        <VersionIcon />
      )}
      {topBarAccessory}
      <RedoUndoContainer>
        <IconButton blocked={!currentUserIsDesignUser} title="Undo">
          <UndoArrow
            onClick={!currentUserIsDesignUser ? triggerPreventionToast : undo}
          />
        </IconButton>
        <IconButton blocked={!currentUserIsDesignUser} title="Redo">
          <RedoArrow
            onClick={!currentUserIsDesignUser ? triggerPreventionToast : redo}
          />
        </IconButton>
      </RedoUndoContainer>
      <Address data-testid="address">
        <label>{homeowner.firstName + ` ` + homeowner.lastName}</label>
        <span>{address}</span>
      </Address>
      <RotatePanelsButton />
      <Gap />
      {moduleDropdown && <ModuleSelectionDropdownForDesigner_OS />}
      {mountType && <MountTypeDropdown />}
      {notifications && (
        <>
          <NotificationBarContainer />
          <NotificationIconForDesigner />
        </>
      )}
      <Settings />
      {designRequest && (
        <NotePad
          style={{ cursor: `pointer`, minWidth: `20px` }}
          onClick={() => setNotesDrawerIsOpen(true)}
        />
      )}
    </StyledTopBar>
  )
}

export const TopBar = memo(TopBar_Internal)
